/**
 * FlightDeck — the redesigned right-rail optimization console.
 *
 * Locked spec (Feb 2026 redesign):
 *   ─────────────────────────────────────────────────
 *   ✦ FLIGHT DECK
 *     One tap to fire. Cost + Pulse lift shown up front.
 *
 *   RECOMMENDED NOW                              (3)
 *   ▸ ✦ Patch L — Bridge                          ↑+5  Free
 *     Mint a brand-new ad-hoc burst from idle…
 *     [Apply  → ]
 *
 *   AMPLIFY                                      (5)
 *   • Hero Mirror                          Free  →
 *   • Reply Storm                          Free  →
 *   • Spawn Burst                       ~$1.2K  →
 *   …
 *
 *   INTERVENE                                    (5)
 *   • Pivot Angle                          Free  →
 *   • Pin CTA Comment                      Free  →
 *   …
 *   ─────────────────────────────────────────────────
 *
 * Color rules:
 *   • Amplify dot = TEAL  (#00D4FF)
 *   • Intervene dot = AMBER (#fbbf24) per user feedback
 *   • CMD recommendation accent = PURPLE (#A78BFA)
 *
 * Behavior (locked decision #5): row click → inline-expand the panel
 * below it (push content down). NOT a tooltip, NOT a modal.
 */
import React, { useState } from 'react';
import {
  Sparkles, Pause, MessageCircle, Repeat2, Plus, Camera, Shuffle, Flag, Pin,
  Clock3, ArrowRightLeft, Radio, ChevronRight, Zap,
} from 'lucide-react';

const CMD_PURPLE   = '#A78BFA';
const AMPLIFY      = '#00D4FF';
const INTERVENE    = '#fbbf24';

// ─── Lever catalog (group + cost + why-it-works) ────────────────────
export const FLIGHT_ACTIONS = [
  { id: 'hero_mirror',     path: 'hero-mirror',     label: 'Hero Mirror',        icon: Repeat2,        cost: 'Free',           costNumeric: 0,    group: 'amplify',   pulseLift: '+2',  why: 'Brand re-shares top post within 7 min → +2.1× reach historical avg.' },
  { id: 'reply_storm',     path: 'reply-storm',     label: 'Reply Storm',        icon: MessageCircle,  cost: 'Free',           costNumeric: 0,    group: 'amplify',   pulseLift: '+3',  why: 'Engagement prompters reply-storm the top post comments — re-ignites algo signal.' },
  { id: 'spawn_burst',     path: 'spawn-burst',     label: 'Spawn Burst',        icon: Plus,           cost: '~$1.2K/creator', costNumeric: 1200, group: 'amplify',   pulseLift: '+5',  why: 'Pull new creators from waiting list to post within 2–4 hrs.' },
  { id: 'story_extension', path: 'story-extension', label: 'Story Extension',    icon: Camera,         cost: '$150 bonus',     costNumeric: 150,  group: 'amplify',   pulseLift: '+2',  why: 'Ask contracted creators to post Story/Reel follow-ups beyond original deliverable.' },
  { id: 'whitelist_spark', path: 'whitelist-spark', label: 'Whitelist Spark Ad', icon: Flag,           cost: 'Brand ad $',     costNumeric: 500,  group: 'amplify',   pulseLift: '+4',  why: 'Request creator permission to boost top post as paid Spark / Partnership Ad.' },
  { id: 'pivot_angle',     path: 'pivot-angle',     label: 'Pivot Angle',        icon: Shuffle,        cost: 'Free',           costNumeric: 0,    group: 'intervene', pulseLift: '+2',  why: 'Change not-yet-posted creators\' angle (Tutorial→Confession, POV→Reaction).' },
  { id: 'pin_comment_cta', path: 'pin-comment-cta', label: 'Pin CTA Comment',    icon: Pin,            cost: 'Free',           costNumeric: 0,    group: 'intervene', pulseLift: '+1',  why: 'Ask creators to pin a CTA comment ("drop your routine below 👇") for comment-velocity spike.' },
  { id: 'shift_time',      path: 'shift-time',      label: 'Shift Post Time',    icon: Clock3,         cost: 'Free',           costNumeric: 0,    group: 'intervene', pulseLift: '+2',  why: 'Reschedule a not-yet-posted burst to a better algorithm window.' },
  { id: 'creator_swap',    path: 'creator-swap',    label: 'Creator Swap',       icon: ArrowRightLeft, cost: '~$800',          costNumeric: 800,  group: 'intervene', pulseLift: '+3',  why: 'Pull a replacement creator if assigned one went quiet 6+hr before slot.' },
  { id: 'pause_wave',      path: 'pause-wave',      label: 'Pause Wave',         icon: Pause,          cost: 'Refund unsent',  costNumeric: null, group: 'intervene', pulseLift: '—',   why: 'Pause all remaining bursts. Brand-safety issue, PR emergency, or exhausted budget.' },
];

function leverByPath(path) {
  return FLIGHT_ACTIONS.find(a => a.path === path);
}

// ─── Recommended Now card ────────────────────────────────────────────
function RecommendedNowRow({ suggestion, onAction }) {
  const lever = leverByPath(suggestion.path);
  const isCmd = suggestion.cmd_flag;
  const accent = isCmd ? CMD_PURPLE : '#FF4D94';

  return (
    <div
      data-testid={`suggestion-${suggestion.action_id}`}
      className="rounded-xl overflow-hidden"
      style={{
        background: isCmd
          ? 'linear-gradient(135deg, rgba(167,139,250,0.16), rgba(0,212,255,0.04))'
          : 'linear-gradient(135deg, rgba(255,77,148,0.14), rgba(0,212,255,0.04))',
        border: `1px solid ${accent}44`,
      }}
    >
      <div className="px-3 pt-3 pb-2">
        {/* Tag row */}
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 text-[8px] uppercase tracking-[0.16em] font-black"
                style={{ color: accent }}>
            <Sparkles className="h-2.5 w-2.5" />
            {isCmd ? 'CMD rec' : 'High-signal'}
          </span>
          <div className="flex items-center gap-1.5">
            {lever?.pulseLift && (
              <span className="text-[9px] font-black tabular-nums px-1.5 py-0.5 rounded"
                    style={{ background: '#4ade8014', color: '#4ade80', border: '1px solid #4ade8033' }}>
                ↑ {lever.pulseLift}
              </span>
            )}
            <span className="text-[9px] font-black tabular-nums px-1.5 py-0.5 rounded"
                  style={{ background: '#0F0F0F', color: '#9CA3AF', border: '1px solid #2E3240' }}>
              {lever?.cost || '—'}
            </span>
          </div>
        </div>
        <p className="mt-1 text-[12px] text-white font-bold leading-snug">{suggestion.title}</p>
        <p className="mt-0.5 text-[10.5px] text-gray-400 leading-snug">{suggestion.detail}</p>
      </div>
      <button
        type="button"
        onClick={() => onAction(suggestion.path, suggestion.cmd_recommendation_id || null)}
        data-testid={`suggestion-${suggestion.action_id}-apply`}
        className="w-full inline-flex items-center justify-between px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition-colors hover:brightness-125"
        style={{
          background: `${accent}1A`,
          color: accent,
          borderTop: `1px solid ${accent}33`,
        }}
      >
        <span className="inline-flex items-center gap-1.5"><Zap className="h-3 w-3" /> Apply</span>
        <ChevronRight className="h-3 w-3" />
      </button>
    </div>
  );
}

// ─── Single lever row (group view) ───────────────────────────────────
function LeverRow({ action, expanded, onToggle, onFire, dotColor }) {
  const Icon = action.icon;
  return (
    <div
      data-testid={`flight-action-${action.id}`}
      className="rounded-lg overflow-hidden"
      style={{ background: '#0A0A0A', border: '1px solid #1f1f1f' }}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`flight-action-${action.id}-row`}
        className="w-full px-2.5 py-2 flex items-center gap-2.5 hover:bg-[#101010] transition-colors"
      >
        {/* 6×6 colored dot — Amplify=teal, Intervene=amber */}
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ background: dotColor, boxShadow: `0 0 6px ${dotColor}99` }}
        />
        <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: dotColor }} />
        <span className="flex-1 text-left text-[11.5px] text-white font-semibold truncate">
          {action.label}
        </span>
        <span
          className="text-[9px] font-bold tabular-nums px-1.5 py-0.5 rounded flex-shrink-0"
          style={{
            background: action.costNumeric === 0 ? '#4ade8014' : '#1f1f1f',
            color: action.costNumeric === 0 ? '#4ade80' : '#cbd5e1',
            border: action.costNumeric === 0 ? '1px solid #4ade8033' : '1px solid #2E3240',
          }}
        >
          {action.costNumeric === 0 ? 'Free' : action.cost.replace('~', '')}
        </span>
        <ChevronRight
          className={`h-3 w-3 text-gray-500 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
      </button>

      {expanded && (
        <div
          className="px-3 pb-3 pt-1"
          data-testid={`flight-action-${action.id}-expand`}
        >
          <p className="text-[10.5px] text-gray-400 leading-relaxed">{action.why}</p>
          <div className="mt-2 flex items-center gap-2">
            {action.pulseLift && (
              <span className="text-[9px] font-black tabular-nums px-1.5 py-0.5 rounded"
                    style={{ background: '#4ade8014', color: '#4ade80', border: '1px solid #4ade8033' }}>
                ↑ {action.pulseLift} Pulse
              </span>
            )}
            <span className="text-[9px] font-black tabular-nums px-1.5 py-0.5 rounded"
                  style={{ background: '#1f1f1f', color: '#cbd5e1', border: '1px solid #2E3240' }}>
              {action.cost}
            </span>
          </div>
          <button
            type="button"
            onClick={onFire}
            data-testid={`flight-action-${action.id}-fire`}
            className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-black uppercase tracking-[0.14em] hover:brightness-110 transition-all"
            style={{ background: dotColor, color: '#0a0a0a' }}
          >
            <Zap className="h-3 w-3" /> Fire {action.label}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Group (Amplify / Intervene) ─────────────────────────────────────
function LeverGroup({ label, color, actions, expanded, setExpanded, onAction, sublabel }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[9px] uppercase tracking-[0.18em] font-black" style={{ color }}>
          {label}
        </p>
        {sublabel && (
          <p className="text-[8px] uppercase tracking-[0.12em] text-gray-600">{sublabel}</p>
        )}
      </div>
      <div className="space-y-1">
        {actions.map(a => (
          <LeverRow
            key={a.id}
            action={a}
            dotColor={color}
            expanded={expanded === a.id}
            onToggle={() => setExpanded(expanded === a.id ? null : a.id)}
            onFire={() => { setExpanded(null); onAction(a.path); }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── FlightDeck — top-level component ────────────────────────────────
export default function FlightDeck({ onAction, suggestions = [], waveStatus }) {
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const isPaused = waveStatus === 'paused';
  const amplify   = FLIGHT_ACTIONS.filter(a => a.group === 'amplify');
  const intervene = FLIGHT_ACTIONS.filter(a => a.group === 'intervene');

  return (
    <aside
      className="rounded-2xl space-y-4 lg:sticky lg:top-6 self-start p-4"
      style={{ background: '#0F0F0F', border: '1px solid #1f1f1f' }}
      data-testid="flight-deck"
    >
      {/* Header */}
      <header>
        <p className="text-[10px] uppercase tracking-[0.2em] font-black flex items-center gap-1.5"
           style={{ color: CMD_PURPLE }}>
          <Sparkles className="h-3 w-3" /> Flight Deck
        </p>
        <p className="text-[10.5px] text-gray-500 mt-1 leading-relaxed">
          One tap to fire. Cost and Pulse lift shown up front.
        </p>
      </header>

      {/* Paused → Resume CTA */}
      {isPaused && (
        <button
          type="button"
          onClick={() => onAction('resume-wave')}
          data-testid="flight-action-resume"
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-green-500/15 border border-green-500/40 text-green-400 text-[11px] font-bold hover:bg-green-500/25 transition-colors"
        >
          <Radio className="h-3.5 w-3.5" /> Resume Wave
        </button>
      )}

      {/* RECOMMENDED NOW — the only thing visible by default */}
      {suggestions.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <p className="text-[9px] uppercase tracking-[0.18em] text-[#FF4D94] font-black">
              Recommended now
            </p>
            <span className="text-[9px] font-black text-gray-500 tabular-nums">
              ({suggestions.length})
            </span>
          </div>
          <div className="space-y-1.5">
            {suggestions.map(s => (
              <RecommendedNowRow key={s.action_id} suggestion={s} onAction={onAction} />
            ))}
          </div>
        </div>
      ) : (
        <div
          className="rounded-xl px-3 py-3 text-center"
          style={{ background: '#0A0A0A', border: '1px dashed #1f1f1f' }}
          data-testid="flight-deck-empty-recs"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] font-black text-gray-500">No active recommendations</p>
          <p className="text-[10.5px] text-gray-400 mt-1 leading-snug">All teams firing within band.</p>
        </div>
      )}

      {/* SHOW ALL 10 LEVERS toggle — replaces the previously-always-visible
          AMPLIFY + INTERVENE groups. Brand reps land on a clean "what to
          do right now" panel; the full lever library is one tap away. */}
      <button
        type="button"
        onClick={() => setShowAll(v => !v)}
        data-testid="flight-deck-toggle-all"
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-[0.14em] transition-colors"
        style={{
          background: showAll ? '#181A20' : '#0A0A0A',
          color: showAll ? '#fff' : '#cbd5e1',
          border: '1px solid #2E3240',
        }}
      >
        <span className="inline-flex items-center gap-1.5">
          {showAll ? 'Hide lever library' : 'Show all 10 levers'}
        </span>
        <ChevronRight className={`h-3 w-3 transition-transform ${showAll ? 'rotate-90' : ''}`} />
      </button>

      {/* AMPLIFY + INTERVENE — collapsed by default */}
      {showAll && (
        <div className="space-y-4" data-testid="flight-deck-all-levers">
          <LeverGroup
            label="Amplify"
            color={AMPLIFY}
            sublabel={`${amplify.length} levers`}
            actions={amplify}
            expanded={expanded}
            setExpanded={setExpanded}
            onAction={onAction}
          />
          <div className="border-t border-[#1a1a1a]" />
          <LeverGroup
            label="Intervene"
            color={INTERVENE}
            sublabel={`${intervene.length} levers`}
            actions={intervene}
            expanded={expanded}
            setExpanded={setExpanded}
            onAction={onAction}
          />
        </div>
      )}
    </aside>
  );
}
